/**
 * Production Dates: every supplier production date across open workspaces, with the install
 * (or delivery) date beside it. View only; dates are set on the workspace's Supplier stage.
 */
import { isFinalSurveyOperational } from '../../modules/docs/utils/surveyDelta';
import { shiftDayKey, toInstallDateOnly } from './jobCardInstallDate';

export type ProductionProject = {
  id: string;
  project_name: string | null;
  customer_name: string | null;
  lifecycle_ref?: string | null;
  supply_only?: boolean | null;
  project_status?: string | null;
  complete?: boolean | null;
  workflow_tasks?: Array<{
    task: string;
    productionDates?: Array<{
      id: string;
      productType?: string;
      supplierId?: string;
      date?: string;
      paid?: string | null;
      reference?: string | null;
      deliveryTo?: string | null;
      dateStatus?: string | null;
    }>;
  }> | null;
};

export type ProductionRow = {
  id: string;
  projectId: string;
  customer: string;
  workspace: string;
  ref: string | null;
  productType: string;
  supplier: string;
  /** YYYY-MM-DD, or null when not set yet. */
  date: string | null;
  paid: 'Deposit' | 'Fully' | null;
  /** The supplier's own order reference. */
  reference: string | null;
  deliveryTo: 'yard' | 'site' | null;
  /** The supplier hasn't confirmed the date yet. */
  provisional: boolean;
  supplyOnly: boolean;
  /** First install (or delivery) day, when booked. */
  bookedDay: string | null;
  /** Produced after the install/delivery date: the booking needs moving or chasing. */
  afterBooking: boolean;
  /** Final survey accepted but no production date entered yet: the order needs placing or chasing. */
  awaiting: boolean;
};

function hasProductionEntries(project: ProductionProject): boolean {
  return (project.workflow_tasks || []).some((task) => task?.task === 'Production Date' && (task.productionDates || []).length > 0);
}

function projectNames(project: ProductionProject) {
  return {
    customer: String(project.customer_name || '').trim() || String(project.project_name || '').trim() || 'Untitled',
    workspace: String(project.project_name || '').trim(),
    ref: project.lifecycle_ref || null,
  };
}

export type FinalSurveyRow = { id: string; project_id: string; option_id?: string | null; completion_status?: string | null };
export type FinalDeltaRow = {
  final_survey_id: string;
  classification?: string | null;
  customer_accepted_at?: string | null;
  reapproval_transmission_version_id?: string | null;
  completed_at?: string | null;
};

/**
 * Workspaces whose final survey is accepted (operational): complete, and either no customer
 * reapproval was needed or the customer has accepted. Uses the main (non-option) final survey
 * and its latest live delta, as the Quotes stage does.
 */
export function finalAcceptedProjectIds(surveys: FinalSurveyRow[], deltas: FinalDeltaRow[]): Set<string> {
  const latestDelta = new Map<string, FinalDeltaRow>();
  deltas.forEach((delta) => {
    const current = latestDelta.get(delta.final_survey_id);
    if (!current || (Date.parse(delta.completed_at || '') || 0) >= (Date.parse(current.completed_at || '') || 0)) {
      latestDelta.set(delta.final_survey_id, delta);
    }
  });
  const byProject = new Map<string, FinalSurveyRow[]>();
  surveys.forEach((survey) => byProject.set(survey.project_id, [...(byProject.get(survey.project_id) || []), survey]));
  const accepted = new Set<string>();
  byProject.forEach((rows, projectId) => {
    const main = rows.find((row) => !row.option_id) || rows[0];
    const delta = latestDelta.get(main.id);
    if (isFinalSurveyOperational({
      completionStatus: main.completion_status,
      latestClassification: (delta?.classification || undefined) as Parameters<typeof isFinalSurveyOperational>[0]['latestClassification'],
      customerAcceptedAt: delta?.customer_accepted_at,
      reapprovalTransmissionVersionId: delta?.reapproval_transmission_version_id,
    })) accepted.add(projectId);
  });
  return accepted;
}

/** Open workspaces whose final survey is accepted but which have no production date entries. */
export function awaitingProductionRows(
  projects: ProductionProject[],
  finalAcceptedIds: Set<string>,
  bookedDayByProject: Map<string, string>,
): ProductionRow[] {
  return projects
    .filter((project) => isOpenProject(project) && finalAcceptedIds.has(project.id) && !hasProductionEntries(project))
    .map((project) => ({
      id: `${project.id}:awaiting`,
      projectId: project.id,
      ...projectNames(project),
      productType: '',
      supplier: '',
      date: null,
      paid: null,
      reference: null,
      deliveryTo: null,
      provisional: false,
      supplyOnly: !!project.supply_only,
      bookedDay: bookedDayByProject.get(project.id) || null,
      afterBooking: false,
      awaiting: true,
    }));
}

/** Open workspaces only: cancelled, completed and closed ones are left out. */
export function isOpenProject(project: ProductionProject): boolean {
  const status = String(project.project_status || '').toLowerCase();
  return project.complete !== true && status !== 'cancelled' && status !== 'completed';
}

export function productionRows(
  projects: ProductionProject[],
  supplierNames: Map<string, string>,
  bookedDayByProject: Map<string, string>,
): ProductionRow[] {
  return projects.filter(isOpenProject).flatMap((project) => {
    const entries = (project.workflow_tasks || [])
      .filter((task) => task.task === 'Production Date')
      .flatMap((task) => task.productionDates || []);
    const bookedDay = bookedDayByProject.get(project.id) || null;
    return entries.map((entry, index) => {
      const date = toInstallDateOnly(entry.date);
      return {
        id: `${project.id}:${entry.id || index}`,
        projectId: project.id,
        ...projectNames(project),
        productType: String(entry.productType || '').trim() || 'Product',
        supplier: supplierNames.get(String(entry.supplierId || '')) || 'Supplier not set',
        date,
        paid: entry.paid === 'Deposit' || entry.paid === 'Fully' ? entry.paid : null,
        reference: String(entry.reference || '').trim() || null,
        deliveryTo: entry.deliveryTo === 'yard' || entry.deliveryTo === 'site' ? entry.deliveryTo : null,
        provisional: entry.dateStatus === 'provisional',
        supplyOnly: !!project.supply_only,
        bookedDay,
        afterBooking: !!date && !!bookedDay && date > bookedDay,
        awaiting: false,
      };
    });
  });
}

export type ProductionSectionId = 'awaiting' | 'this_week' | 'next_week' | 'later' | 'no_date' | 'recent';

export const PRODUCTION_SECTIONS: Array<{ id: ProductionSectionId; title: string; note: string }> = [
  { id: 'awaiting', title: 'Final survey accepted, no production date', note: 'Ready to order: add the production date on the Supplier stage.' },
  { id: 'this_week', title: 'This week', note: 'Today to Sunday.' },
  { id: 'next_week', title: 'Next week', note: 'Monday to Sunday.' },
  { id: 'later', title: 'Later', note: 'After next week.' },
  { id: 'no_date', title: 'No date yet', note: 'Supplier entry added without a production date.' },
  { id: 'recent', title: 'Last 2 weeks', note: 'Production dates that have passed.' },
];

/** Production dates further back than this are left off. */
export const RECENT_PRODUCTION_DAYS = 14;

export function productionSection(date: string | null, today: string): ProductionSectionId | null {
  if (!date) return 'no_date';
  if (date < today) return date >= shiftDayKey(today, -RECENT_PRODUCTION_DAYS) ? 'recent' : null;
  const dayOfWeek = (new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7; // Monday 0
  const sunday = shiftDayKey(today, 6 - dayOfWeek);
  if (date <= sunday) return 'this_week';
  if (date <= shiftDayKey(sunday, 7)) return 'next_week';
  return 'later';
}

export function sectionProductionRows(rows: ProductionRow[], today: string) {
  const buckets = new Map<ProductionSectionId, ProductionRow[]>();
  rows.forEach((row) => {
    const section = row.awaiting ? 'awaiting' : productionSection(row.date, today);
    if (section) buckets.set(section, [...(buckets.get(section) || []), row]);
  });
  return PRODUCTION_SECTIONS
    .map((section) => {
      const items = (buckets.get(section.id) || []).slice().sort((a, b) => {
        // Awaiting: soonest install first, unbooked jobs last
        if (section.id === 'awaiting') return (a.bookedDay || '9999').localeCompare(b.bookedDay || '9999') || a.customer.localeCompare(b.customer);
        return (section.id === 'recent'
          ? (b.date || '').localeCompare(a.date || '')
          : (a.date || '').localeCompare(b.date || '')) || a.customer.localeCompare(b.customer);
      });
      return { ...section, rows: items };
    })
    .filter((section) => section.rows.length > 0);
}

export function productionMatches(row: ProductionRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [row.customer, row.workspace, row.ref, row.productType, row.supplier, row.reference]
    .some((value) => String(value || '').toLowerCase().includes(needle));
}
